import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import styles from './bulkUploadUsers.module.scss';
import { DragDropFile } from "../../../components/Drag&DropFile/Drag&DropFile";
import { requests } from "../../../services/http-common";
import UploadingLoading from "./UploadingLoading";
import TableHead, { THeaderConfigItem } from "./Table/TableHead";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Nullable } from "primereact/ts-helpers";
import TableBody from "./Table/TableBody";
import ButtonPrimary from "../../../uiComponents/ButtonPrimary/ButtonPrimary";
import { AxiosError } from "axios";
import { extractErrorMessage } from "../../../helpers/extractErrorMessage";
import { useToast } from "../../../hooks/useToast";
import usePagination from "../../../hooks/usePagination";
import DraftUsers from "../DraftUsers/DraftUsers";
import Loading from "../../../components/Loading/Loading";
import AddRowManually from "./AddRowManually/AddRowManually";
import * as Yup from "yup";
import { classNames } from 'primereact/utils';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { HttpService } from '../../../services/HttpService';
import { Toast } from 'primereact/toast';
import { ReactPdfViewer } from '../../../components/ReactPdfViewer/ReactPdfViewer';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';


interface TBulkUploadUsersProps {
    companyId: number;
    isUploadPdfEnabled: boolean;
    skipGrantHeaderRecognizing: boolean;
}

type TConfig = {
    data: Record<string, string>[];
    headers: THeaderConfigItem[];
    uniqueKey: string;
};

type TOnRecognizeParams = {
    headers: Record<string, string>;
    uniqueKey: string;
}

export type THeaderStateItem = { key: string, label: string };
export type THeaderState = Record<string, THeaderStateItem>;

const BulkUploadUsers: FC<TBulkUploadUsersProps> = ({ companyId, skipGrantHeaderRecognizing, isUploadPdfEnabled }) => {
    const { params, changePage, changePerPage } = usePagination();
    const { page, size, sort } = params;
    const toast = useRef<Toast | null>(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const [isSigendfileUploaded, setSigendfileUploaded] = useState(false);
    const [config, updateConfig] = useState<Nullable<TConfig>>(null);
    const [uploading, setUploading] = useState(false);
    const [headerState, updateHeaderState] = useState<Nullable<THeaderState>>(null);
    const [order, setOrder] = useState<THeaderConfigItem[]>([]);
    const [isSigendfileUploading, setSignedFileUploading] = useState<boolean>(false);
    const pickedFileRef = useRef<Nullable<Blob>>(null);
    const [formData, setFormData] = useState<FieldValues>({});
    const [showConfirm, setShowConfirm] = useState(false);
    const { show } = useToast();
    const navigate = useNavigate();
    const data = config?.data || [];
    const headers = config?.headers || [];
    const uniqueKey = config?.uniqueKey || '';
    const {
        control,
        reset,
        setError,
        handleSubmit,
        formState: { errors },
    } = useForm({
        mode: 'onChange',
        resolver: yupResolver(Yup.object().shape({
            name: Yup.string().required("This field is required"),
            email: Yup.string().email("Invalid email address").required("This field is required"),
            grantDate: Yup.date().typeError('This field is required').required("This field is required"),
        })),
    });
    const setUploadingInitialStates = () => {
        updateConfig(null);
        updateHeaderState(null);
        setOrder([]);
    }

    const searchUrl = useMemo(() => {
        const pageStr = `page=${page}&size=${size}`;
        const sortFormatted =
            sort.length > 0 ? sort.map((str) => `sort=${str}`) : [];
        const sortStr = sort.length > 0 ? `&${sortFormatted.join("&")}` : "";
        const concatUrl = `${pageStr}${sortStr}`;
        return `?${concatUrl}`;
    }, [page, size, sort]);

    const {
        data: draftUsers,
        isLoading: draftUsersLoading,
        refetch: refetchDraftUsers,
    } = useQuery({
        queryKey: ["draft-file83b-users", page, size, sort.join(""), companyId],
        queryFn: async () => {
            return await requests.get(
                `/api/companies/${companyId}/file83b-draft${searchUrl}`
            );
        },
        staleTime: Infinity,
        gcTime: 0,
        retry: true,
    });
    const totalElements = draftUsers?.totalElements || 0;
    const draftUsersList = draftUsers?.content || [];
    const pageCount = Math.ceil(totalElements / size);

    const {
        data: draftUsersConfiguration,
    } = useQuery({
        queryKey: ["draft-users-configuration", page, size, sort.join(""), companyId],
        queryFn: async () => {
            return await requests.get(
                `/api/companies/${companyId}/file83b-draft/configuration`
            );
        },
        staleTime: Infinity,
        gcTime: 0,
        retry: true,
    });
    const columnHeaders = draftUsersConfiguration?.columnHeaders || [];

    const { data: csvHeaderOptions } = useQuery({
        queryKey: ["csv-head-option"],
        queryFn: async () => {
            return requests.get(`/api/companies/${companyId}/csv/configs`);
        },
        gcTime: 0,
    });

    const convertConfigToValues = (data: THeaderConfigItem[]) => {
        const columnOrder: THeaderConfigItem[] = [];
        const initialHeaderStateValue: THeaderState = {};
        data.forEach((item: THeaderConfigItem) => {
            const { name, label, key } = item;
            initialHeaderStateValue[name] = { key, label };
            columnOrder.push(item);
        });
        return { initialHeaderStateValue, columnOrder };
    }

    const uploadAction = async (file: Blob) => {
        try {
            const formData = new FormData();
            formData.append("file", file);
            const uploadType = skipGrantHeaderRecognizing ? 'upload-directly' : 'upload';
            const url = `/api/companies/${companyId}/csv/${uploadType}`;
            const response = await requests.post(url, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            if (skipGrantHeaderRecognizing) {
                refetchDraftUsers().then(() => {
                    setUploading(false);
                    pickedFileRef.current = null;
                });
            } else {
                updateConfig(response);
                const { initialHeaderStateValue, columnOrder } = convertConfigToValues(response?.headers || []);
                updateHeaderState(initialHeaderStateValue);
                setOrder(columnOrder);
                setUploading(false);
                pickedFileRef.current = null;
            }
        } catch (error: any) {
            const errorData = {
                error: error,
                variables: pickedFileRef.current,
                retryFn: uploadAction,
                show: show,
                cb: () => {
                    setUploading(false);
                    pickedFileRef.current = null;
                }
            };
            extractErrorMessage(errorData);
        }
    };

    const onRecognizeHeaders = useMutation({
        mutationFn: async (params: TOnRecognizeParams) => {
            return await requests.post(
                `api/companies/${companyId}/csv/recognize`, params
            );
        },
        onSuccess: () => {
            refetchDraftUsers();
            setUploadingInitialStates();
        },
        onError: (error: AxiosError<{ detail: string }>, variables) => {
            const errorData = {
                error: error,
                variables: variables,
                retryFn: onRecognizeHeaders.mutate,
                show: show,
            };
            extractErrorMessage(errorData);
        },
    });

    const onSelect = (files: Blob[]) => {

        if (files && files[0]) {

            const file = files[0];
            pickedFileRef.current = file;

            if (file.type === "application/pdf" && isUploadPdfEnabled) {
                const fileURL = URL.createObjectURL(file);
                setPreviewUrl(fileURL)
                setSigendfileUploaded(true);
            } else {
                setUploading(true);
                void uploadAction(file);
            }
        }
    };

    const onChangeColumnName = (key: string, value: THeaderStateItem) => {
        updateHeaderState((prev) => ({ ...prev, [key]: value }));
    }

    const onSubmitColumnOrder = () => {
        const obj: Record<string, string> = {};
        (Object.keys(headerState || {})).forEach((columnName) => {
            if (headerState) {
                obj[columnName] = headerState[columnName].key;
            }

        });
        const params = {
            headers: obj,
            uniqueKey,
        };
        onRecognizeHeaders.mutate(params);
    }

    const onClearTempData = () => {
        pickedFileRef.current = null;
        updateConfig(null);
        updateHeaderState(null);
        setOrder([]);
        setUploading(false);
    }

    const exportCSV = useMutation({
        mutationFn: async () => {
            return await requests.get(
                `api/companies/${companyId}/csv/csv-example`,
            );
        },
        onSuccess: (csvStr) => {
            const fileName = localStorage.getItem('tempFileName') || 'data.csv';
            const blob = new Blob([csvStr], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(blob);
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            localStorage.removeItem('tempFileName');
        },
        onError: (error: AxiosError<{ message: string }>, variables) => {
            const errorData = {
                error: error,
                variables: variables,
                retryFn: exportCSV.mutate,
                show: show,
            };
            extractErrorMessage(errorData);
        },
    });

    const onDownload = () => {
        exportCSV.mutate();
    }

    const onSubmitHandler = useCallback(async (data: FieldValues) => {

        setFormData(data);
        setShowConfirm(true);
    }, [setFormData]);

    const handleUploadSignedDoc = useCallback(async (formData: FieldValues) => {

        try {
            setSignedFileUploading(true);
            const { data: { fileName, url } } = await HttpService.get(
                `/api/companies/${companyId}/form-textract/upload-url`
            );

            await fetch(url, {
                method: 'PUT',
                body: pickedFileRef.current,
                headers: {
                    'Content-Type': pickedFileRef.current?.type,
                } as HeadersInit,
            });

            await HttpService.put(
                `/api/companies/${companyId}/form-textract/approve`,
                {
                    ...formData,
                    type: "FILE_83B",
                    fileName
                }
            );

            toast.current?.show({
                severity: "success",
                summary: "Success",
                life: 1000,
                detail: "Document successfully uploaded"
            });
            reset();
            pickedFileRef.current = null;
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                const message = error?.response?.data?.detail;
                setError("email", {
                    type: "manual",
                    message
                });
            }
        } finally {
            setSignedFileUploading(false);
        }

    }, [companyId, pickedFileRef]);

    const dateRange = useMemo(() => {
        const range = 14;
        const minDate = new Date();
        const maxDate = new Date();
        minDate.setDate(minDate.getDate() - range);
        maxDate.setDate(maxDate.getDate() + 1);
        return {
            minDate,
            maxDate,
        }
    }, []);

    if (draftUsersLoading) {
        return (
            <div className={styles.mainLoadingWrapper}>
                <Loading horizontalSpacing={250} />
            </div>
        )
    }

    if (draftUsersList.length > 0) {
        return (
            <DraftUsers
                draftUsersList={draftUsersList}
                page={page}
                size={size}
                currentLength={draftUsersList.length}
                totalElements={totalElements}
                changePage={changePage}
                pageCount={pageCount}
                changePerPage={changePerPage}
                columnHeaders={columnHeaders}
                companyId={companyId}
                refetchDraftUsers={refetchDraftUsers}
            />
        );
    }

    if (uploading) {
        return (
            <UploadingLoading />
        );
    }

    if (isSigendfileUploaded) {
        return (
            <div className="overflow-hidden" style={{ height: "calc(100vh - 90px)", maxHeight: "calc(100vh - 90px)" }}>
                <Toast ref={toast} onHide={() => navigate("/admin-dashboard")} />
                <ConfirmDialog
                    header="Upload and Invite"
                    message="You are about to invite this filer to review and confirm the uploaded document.  Do you wish to proceed?"
                    visible={showConfirm}
                    onHide={() => setShowConfirm(false)}
                    accept={() => handleUploadSignedDoc(formData)}
                    reject={() => {
                        reset();
                        pickedFileRef.current = null;
                        setShowConfirm(false);
                        navigate('/admin-dashboard');
                    }}
                />
                <form className="w-full h-full max-h-full flex gap-2 border-100 border-top-2 p-5 pb-0 overflow-hidden" onSubmit={handleSubmit(onSubmitHandler)}>
                    <div className="flex flex-column row-gap-5 sm:col-5 md:col-4 lg:col-3 xl:col-3 bg-white p-3 max-h-full">
                        <div className="text-lg font-bold">
                            Please provide the filer’s name and email address.
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="name" className={classNames({ "text-red-500": errors.name })}>
                                Name
                                <span className="ml-1 text-red-300">*</span>
                            </label>
                            <Controller
                                name="name"
                                control={control}
                                render={({ field, fieldState }) => {
                                    return (
                                        <>
                                            <InputText {...field}
                                                id={field.name}
                                                className={classNames("p-inputtext-sm", {
                                                    'p-invalid': fieldState.invalid,
                                                    'text-red-500': fieldState.invalid,
                                                })} />
                                            {
                                                fieldState.invalid ? (
                                                    <small className="text-red-500">
                                                        {fieldState.error?.message}
                                                    </small>
                                                ) : null
                                            }
                                        </>
                                    )
                                }} />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="email" className={classNames({ "text-red-500": errors.name })}>
                                Email
                                <span className="ml-1 text-red-300">*</span>
                            </label>
                            <Controller
                                name="email"
                                control={control}
                                render={({ field, fieldState }) => {
                                    return (
                                        <>
                                            <InputText {...field}
                                                id={field.name}
                                                className={classNames("p-inputtext-sm", {
                                                    'p-invalid': fieldState.invalid
                                                })} />
                                            {
                                                fieldState.invalid ? (
                                                    <small className="text-red-500">
                                                        {fieldState.error?.message}
                                                    </small>
                                                ) : null
                                            }
                                        </>
                                    )
                                }} />
                        </div>
                        <div className="flex flex-column gap-2">
                            <label htmlFor="email" className={classNames({ "text-red-500": errors.name })}>
                                Date of Transfer
                                <span className="ml-1 text-red-300">*</span>
                            </label>
                            <Controller
                                name="grantDate"
                                control={control}
                                render={({ field, fieldState }) => {
                                    return (
                                        <>
                                            <Calendar
                                                {...field}
                                                value={field.value ? moment(field.value).utc().toDate() : undefined}
                                                appendTo="self"
                                                dateFormat="MM d, yy"
                                                className="w-full"
                                                {...dateRange}

                                                inputClassName={classNames("w-12", {
                                                    'p-invalid': fieldState.invalid,
                                                })}
                                            />
                                            {
                                                fieldState.invalid ? (
                                                    <small className="text-red-500">
                                                        {fieldState.error?.message}
                                                    </small>
                                                ) : null
                                            }
                                        </>
                                    )
                                }} />
                        </div>
                        <div className="flex gap-2 justify-content-end mt-auto">
                            <Button outlined type="reset" label="Cancel" onClick={() => {
                                pickedFileRef.current = null;
                                navigate('/admin-dashboard');
                            }} />
                            <Button type="submit" label="Upload" loading={isSigendfileUploading} />
                        </div>
                    </div>
                    <div className="sm:col-7 md:col-8 lg:col-9 xl:col-9 bg-white p-3 overflow-hidden">
                        <div className="overflow-auto p-2 max-h-full">
                            <ReactPdfViewer fileUrl={previewUrl} isModal={false} />
                        </div>
                    </div>
                </form>
            </div>
        )
    }

    return (
        <div className={styles.bulkUploadUsers}>
            {
                !config ? (
                    <div className={styles.content}>
                        <div className="flex justify-content-between gap-3">
                            <div className="flex-1">
                                <div className={styles.dragDropWrapper}>
                                    <Toast ref={toast} />
                                    <h3>Add Grants</h3>
                                    <DragDropFile
                                        label={`Drag and drop the CSV ${isUploadPdfEnabled ? "or PDF" : ""} file you’d like to upload.`}
                                        onChange={onSelect}
                                        multiple={true}
                                        allowedExtensions={isUploadPdfEnabled ? ".csv, .pdf" : ".csv"}
                                    />
                                    <span>
                                        Please use the linked CSV template to draft the 83(b) elections.
                                        <button type="button" className={styles.downloadTemplate} onClick={onDownload}>
                                            Download
                                        </button>
                                    </span>
                                </div>
                                <div className={styles.addRowManually}>
                                    <AddRowManually
                                        columnHeaders={columnHeaders}
                                        companyId={companyId}
                                        refetchDraftUsers={refetchDraftUsers}
                                        className={styles.addBtn}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={styles.horizontalScroller}>
                        <table className={styles.table}>
                            <TableHead
                                options={headers as THeaderConfigItem[]}
                                csvHeaderOptions={csvHeaderOptions}
                                headerState={headerState}
                                onChangeColumnName={onChangeColumnName}
                            />
                            <TableBody
                                data={data}
                                order={order}
                            />
                        </table>
                    </div>
                )
            }
            {
                config && (
                    <div className={styles.footer}>
                        <ButtonPrimary
                            label="Cancel"
                            styleType="secondaryOutline"
                            onClick={onClearTempData}
                        />
                        <ButtonPrimary
                            label="Next"
                            onClick={onSubmitColumnOrder}
                        />
                    </div>
                )
            }
        </div>
    )
}

export default BulkUploadUsers;
